import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { OrganizationReadModel, UpdateOrganizationRequest } from './types';

export interface UpdateOrganizationArgs {
  data: UpdateOrganizationRequest;
  logo?: File | null;
}

export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL,
  }),
  tagTypes: ['Organization'],
  endpoints: builder => ({
    getOrganizationById: builder.query<OrganizationReadModel, string>({
      query: id => ({ url: `/organizations/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Organization', id }],
    }),
    getOrganizationLogo: builder.query<Blob, string>({
      query: id => ({
        url: `/organizations/${id}/logo`,
        method: 'GET',
        responseType: 'blob',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Organization', id }],
    }),
    updateOrganization: builder.mutation<void, UpdateOrganizationArgs>({
      query: ({ data, logo }) => {
        const formData = new FormData();
        formData.append('json', JSON.stringify(data));
        if (logo) {
          formData.append('logo', logo, logo.name);
        }

        return {
          url: `/organizations/${data.id}`,
          method: 'PUT',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'Organization', id: data.id },
        'Organization',
      ],
    }),
  }),
});

export const {
  useUpdateOrganizationMutation,
  useGetOrganizationByIdQuery,
  useGetOrganizationLogoQuery,
} = organizationApi;
